import React, { useState, useRef } from 'react';

import {
  Box,
  Flex,
  Text,
  Badge,
  Link,
  Alert,
  AlertIcon,
  Tooltip,
  IconButton,
  Input,
  Grid,
  GridItem,
  Image,
  Button,
  VStack,
  HStack,
  useToast,
} from '@chakra-ui/react';
import { CloseIcon, AddIcon, CheckIcon } from '@chakra-ui/icons';

import { useColors } from './colors';

const ImageManagement = ({
  images,
  setImages,
  errorImages,
  setErrorImages,
  getMaxImages,
  listing,
  /** When set, overrides marketplace detection from `listing`. */
  isAmazon: isAmazonProp,
  title = 'Listing Images',
  embedded = false,
}) => {
  const fileInputRef = useRef(null);
  const draggedImageRef = useRef(null);
  const [showUrlPopup, setShowUrlPopup] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [isValidatingUrl, setIsValidatingUrl] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const toast = useToast();

  const {
    bgColor,
    borderColor,
    textColor,
    headingColor,
    hoverBg: emptySlotBg,
    errorColor,
    hoverColor: emptySlotHoverBg,
  } = useColors();

  const emptySlotBorder = borderColor;
  const errorBg = errorColor;

  const MAIN_GRID_CAP = 16;
  const MAIN_IMAGE_SIZE = '325px';
  const REGULAR_IMAGE_SIZE = '100px';

  const maxImages = getMaxImages();
  const mainGridCount = Math.min(maxImages, MAIN_GRID_CAP);
  const overflowCount = Math.max(0, maxImages - MAIN_GRID_CAP);

  const mainGridSlots = Array.from(
    { length: mainGridCount },
    (_, i) => images[i] || null,
  );
  const overflowSlots = Array.from(
    { length: overflowCount },
    (_, i) => images[MAIN_GRID_CAP + i] || null,
  );

  const isAmazon =
    isAmazonProp ??
    listing?.marketplace?.name?.toLowerCase() === 'amazon';

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          let isValid = false;

          if (isAmazon) {
            const longestSide = Math.max(img.width, img.height);
            const shortestSide = Math.min(img.width, img.height);
            isValid = longestSide >= 1000 && shortestSide >= 500 && longestSide <= 10000;
          } else {
            isValid = img.width >= 500 && img.height >= 500;
          }

          const newImage = {
            id: Date.now() + Math.random(),
            url: e.target.result,
            data: e.target.result.split(',')[1],
            name: file.name,
            isMain: images.length === 0,
            isValid,
            width: img.width,
            height: img.height,
            isNew: true,
          };

          setImages((prev) => [...prev, newImage]);

          if (!newImage.isValid) {
            setErrorImages((prev) => [...prev, newImage.id.toString()]);
          }
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });

    event.target.value = '';
  };

  const removeImage = (imageId) => {
    setImages((prev) => {
      const filtered = prev.filter((img) => img.id !== imageId);
      if (filtered.length > 0 && prev.find((img) => img.id === imageId)?.isMain) {
        filtered[0].isMain = true;
      }
      return filtered;
    });
    setErrorImages((prev) => prev.filter((id) => id !== imageId.toString()));
  };

  const handleDragStart = (e, image) => {
    draggedImageRef.current = image;
    e.dataTransfer.setData('text/plain', String(image.id));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetImage) => {
    e.preventDefault();

    const draggedImage = draggedImageRef.current;
    if (!draggedImage || draggedImage.id === targetImage.id) {
      draggedImageRef.current = null;
      return;
    }

    setImages((prev) => {
      const newImages = [...prev];
      const draggedIndex = newImages.findIndex((img) => img.id === draggedImage.id);
      const targetIndex = newImages.findIndex((img) => img.id === targetImage.id);

      const [draggedItem] = newImages.splice(draggedIndex, 1);
      newImages.splice(targetIndex, 0, draggedItem);

      return newImages;
    });

    draggedImageRef.current = null;
  };

  const handleUrlSubmit = async () => {
    if (!urlInput.trim()) return;

    setIsValidatingUrl(true);

    try {
      const img = new window.Image();

      await new Promise((resolve, reject) => {
        img.onload = () => {
          let isValid = false;
          let errorMessage = '';

          if (isAmazon) {
            const longestSide = Math.max(img.width, img.height);
            const shortestSide = Math.min(img.width, img.height);

            if (longestSide < 1000) {
              errorMessage = 'Image must be at least 1000 pixels on the longest side';
            } else if (shortestSide < 500) {
              errorMessage = 'Image must be at least 500 pixels on the shortest side';
            } else if (longestSide > 10000) {
              errorMessage = 'Image must not exceed 10000 pixels on the longest side';
            } else {
              isValid = true;
            }
          } else if (img.width >= 500 && img.height >= 500) {
            isValid = true;
          } else {
            errorMessage = 'Image must be at least 500x500 pixels';
          }

          if (isValid) {
            resolve();
          } else {
            reject(new Error(errorMessage));
          }
        };
        img.onerror = () => reject(new Error('Failed to load image from URL'));
        img.src = urlInput.trim();
      });

      const newImage = {
        id: Date.now() + Math.random(),
        url: urlInput.trim(),
        data: null,
        name: urlInput.split('/').pop() || 'Image from URL',
        isMain: images.length === 0,
        isValid: true,
        width: img.width,
        height: img.height,
        isNew: true,
        isUrl: true,
      };

      setImages((prev) => [...prev, newImage]);

      setUrlInput('');
      setShowUrlPopup(false);

      toast({
        title: 'Image added successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Invalid image URL',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsValidatingUrl(false);
    }
  };

  const handleUrlKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleUrlSubmit();
    }
  };

  const showUrlInput = (event) => {
    const rect = event.target.getBoundingClientRect();
    setPopupPosition({
      top: rect.top - 80,
      left: rect.left,
    });
    setShowUrlPopup(true);
  };

  const ImageSlot = ({ image, index, isEmpty = false }) => {
    const [isHovered, setIsHovered] = useState(false);
    const isMainImage = index === 0;
    const slotSize = isMainImage ? MAIN_IMAGE_SIZE : REGULAR_IMAGE_SIZE;

    if (isEmpty) {
      const shouldShowAddButton = index === images.length && images.length < maxImages;

      const handleAddClick = (e) => {
        if (!shouldShowAddButton) return;

        if (isAmazon) {
          showUrlInput(e);
        } else {
          fileInputRef.current?.click();
        }
      };

      return (
        <Box
          w={slotSize}
          h={slotSize}
          bg={emptySlotBg}
          border="2px solid"
          borderColor={emptySlotBorder}
          borderRadius="lg"
          cursor={shouldShowAddButton ? 'pointer' : 'default'}
          onClick={handleAddClick}
          _hover={
            shouldShowAddButton
              ? {
                  borderColor: 'blue.400',
                  bg: emptySlotHoverBg,
                }
              : {}
          }
          transition="all 0.2s"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {shouldShowAddButton && (
            <Flex direction="column" align="center" justify="center">
              <AddIcon boxSize={4} color="gray.400" mb={1} />
              <Text fontSize="xs" color="gray.400" fontWeight="semibold">
                Add {isAmazon ? 'URL' : ''}
              </Text>
            </Flex>
          )}
        </Box>
      );
    }

    return (
      <Box
        position="relative"
        w={slotSize}
        h={slotSize}
        bg={image.isValid ? 'white' : errorBg}
        border="2px solid"
        borderColor={image.isValid ? 'gray.200' : 'red.300'}
        borderRadius="lg"
        cursor="grab"
        draggable
        onDragStart={(e) => handleDragStart(e, image)}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, image)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        _hover={{
          borderColor: image.isValid ? 'blue.400' : 'red.400',
          transform: 'scale(1.02)',
        }}
        _active={{ cursor: 'grabbing' }}
        transition="all 0.2s"
        overflow="hidden"
      >
        <Image
          src={image.url}
          alt={`Product image ${index + 1}`}
          objectFit="contain"
          w="100%"
          h="100%"
          borderRadius="md"
          style={{ pointerEvents: 'none' }}
        />

        {isMainImage && (
          <Badge
            position="absolute"
            bottom="8px"
            left="50%"
            transform="translateX(-50%)"
            colorScheme="gray"
            variant="solid"
            fontSize="xs"
            px={3}
            py={1}
            borderRadius="md"
            style={{ pointerEvents: 'none' }}
          >
            Main
          </Badge>
        )}

        {!image.isValid && (
          <Tooltip
            label={
              isAmazon
                ? 'Must be 1000px+ on longest side, 500px+ on shortest side, max 10000px'
                : 'Must be over 500 pixels wide'
            }
            placement="top"
            hasArrow
            bg="red.600"
            color="white"
          >
            <Box
              position="absolute"
              top="8px"
              right="8px"
              bg="red.500"
              color="white"
              borderRadius="full"
              w="20px"
              h="20px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="xs"
              fontWeight="bold"
            >
              !
            </Box>
          </Tooltip>
        )}

        {isHovered && (
          <IconButton
            position="absolute"
            top="8px"
            left="8px"
            size="sm"
            colorScheme="gray"
            variant="solid"
            bg="gray.500"
            color="white"
            icon={<CloseIcon boxSize={3} />}
            onClick={() => removeImage(image.id)}
            aria-label="Remove image"
            borderRadius="full"
            _hover={{ bg: 'gray.600' }}
          />
        )}
      </Box>
    );
  };

  return (
    <Box
      bg={bgColor}
      borderRadius={embedded ? 'md' : 'xl'}
      p={embedded ? 4 : 6}
      border="1px solid"
      borderColor={borderColor}
      flex="1"
    >
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="xl" fontWeight="semibold" color={headingColor}>
          {title}
        </Text>
      </Flex>

      <Text fontSize="sm" color={textColor} mb={4}>
        You can add up to {maxImages} images{isAmazon ? ' via URLs' : ''}. Drag to reorder; the
        first image is the main photo.
      </Text>

      {errorImages.length > 0 && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            {errorImages.length} photo{errorImages.length > 1 ? 's' : ''} failed validation. Remove
            photos with errors before saving.
          </Text>
        </Alert>
      )}

      <Text fontSize="sm" color={textColor} mb={6}>
        {images.length}/{maxImages}
      </Text>

      <Flex>
        <Flex direction="column" gap={6}>
          <Box>
            <Flex gap={4} align="flex-start" wrap="wrap">
              <ImageSlot image={mainGridSlots[0]} index={0} isEmpty={!mainGridSlots[0]} />

              <Grid templateColumns="repeat(5, 1fr)" gap={3} flex="1" minW="600px">
                {mainGridSlots.slice(1).map((image, idx) => (
                  <GridItem key={idx + 1}>
                    <ImageSlot image={image} index={idx + 1} isEmpty={!image} />
                  </GridItem>
                ))}
              </Grid>
            </Flex>
          </Box>

          {overflowCount > 0 && images.length >= MAIN_GRID_CAP && (
            <Box>
              <Text fontSize="sm" color={textColor} fontWeight="semibold" mb={4}>
                Additional Images
              </Text>
              <Grid
                templateColumns={`repeat(${Math.min(overflowCount, 8)}, 1fr)`}
                gap={3}
                maxW="1000px"
              >
                {overflowSlots.map((image, idx) => {
                  const slotIndex = MAIN_GRID_CAP + idx;
                  return (
                    <GridItem key={slotIndex}>
                      <ImageSlot image={image} index={slotIndex} isEmpty={!image} />
                    </GridItem>
                  );
                })}
              </Grid>
            </Box>
          )}
        </Flex>
      </Flex>

      <Input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        onChange={handleImageUpload}
        display="none"
      />

      {isAmazon && showUrlPopup && (
        <>
          <Box
            position="fixed"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="blackAlpha.300"
            zIndex="1000"
            onClick={() => {
              setShowUrlPopup(false);
              setUrlInput('');
            }}
          />

          <Box
            position="fixed"
            top={`${popupPosition.top}px`}
            left={`${popupPosition.left}px`}
            bg={bgColor}
            border="2px solid"
            borderColor="blue.400"
            borderRadius="lg"
            p={4}
            boxShadow="xl"
            zIndex="1001"
            minW="300px"
          >
            <VStack spacing={3} align="stretch">
              <Text fontSize="sm" fontWeight="semibold" color={headingColor}>
                Enter Image URL
              </Text>
              <Input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyPress={handleUrlKeyPress}
                placeholder="https://example.com/image.jpg"
                size="sm"
                autoFocus
                _focus={{
                  borderColor: 'blue.400',
                  boxShadow: '0 0 0 1px #3182ce',
                }}
              />
              <HStack spacing={2}>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={handleUrlSubmit}
                  isLoading={isValidatingUrl}
                  loadingText="Validating..."
                  leftIcon={<CheckIcon />}
                  flex="1"
                >
                  Add Image
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowUrlPopup(false);
                    setUrlInput('');
                  }}
                >
                  Cancel
                </Button>
              </HStack>
            </VStack>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ImageManagement;
